import classNames from 'classnames';
import { observer } from 'mobx-react';
import { ButtonHTMLAttributes, HTMLAttributes, useMemo } from 'react';
import ReactModal, { Props as ModalProps } from 'react-modal';

import { store } from 'Models/Store';
import { Button, Colors, Display, ICbButtonProps } from '../Button/Button';

export interface INewModalProps {
	/** Is the modal open */
	isOpen: boolean;
	/** A label for the modal for screen readers */
	label: string;
	/** The classname for the modal */
	className?: string;
	/** The classname for the modal overlay */
	overlayClassName?: string;
	/** Function that will be run after the modal has opened. */
	onAfterOpen?: () => void;
	/** Function that will be run after the modal has closed. */
	onAfterClose?: () => void;
	/**
	 * Function that will be run when the modal is requested to be closed, prior to actually closing.
	 * If the isOpen prop is not changed in this callback then it will not actually close!
	 */
	onRequestClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
	/**
	 * Raw props to be passed to react-modal.
	 * This will overwrite any props that are placed on the modal by this component
	 */
	modalProps?: ModalProps;
	/** Children elements */
	children: React.ReactNode;
	/** Boolean indicating if the overlay should close the modal. Defaults to true. */
	shouldCloseOnOverlayClick?: boolean;
	/** Boolean indicating if pressing the esc key should close the modal */
	shouldCloseOnEsc?: boolean;
	/**
	 * The size of the modal.
	 * - sm: ~400px
	 * - md: ~600px (default)
	 * - lg: ~900px
	 * - xl: ~1200px
	 *
	 * Note: All sizes scale down to 95% width on mobile/tablet screens.
	 */
	modalSize?: 'sm' | 'md' | 'lg' | 'xl';
}

const rootId = 'root';

const modalClassNames = {
	content: 'new-modal-content',
	overlay: 'new-modal-container',
	header: 'new-modal__header',
	title: 'new-modal__title',
	message: 'new-modal__message',
	label: 'new-modal__label',
	section: 'new-modal__section',
	footer: 'new-modal__actions',
	cancel: 'new-modal--cancel',
	confirm: 'new-modal--confirm',
} as const;

/**
 * A modal dialog that can display any content inside of it
 */
function NewModalContainer({
	label,
	overlayClassName,
	onAfterOpen,
	onAfterClose,
	onRequestClose,
	children,
	className,
	modalProps,
	isOpen,
	shouldCloseOnEsc = true,
	shouldCloseOnOverlayClick = true,
	modalSize = 'md',
}: INewModalProps) {
	// Get modal element reference - using useMemo to avoid repeated DOM queries
	const modalElement = useMemo(() => {
		return document.getElementById(rootId);
	}, []);

	// Early return if modal element is not found
	if (!modalElement) {
		console.error(`Could not find the #${rootId} element in the html. Could not create modal`);
		return <></>;
	}

	return (
		<ReactModal
			className={classNames(modalClassNames.content, `${modalClassNames.content}--${modalSize}`, className)}
			overlayClassName={classNames(modalClassNames.overlay, overlayClassName)}
			portalClassName={classNames('portal', store.appLocation)}
			appElement={modalElement}
			isOpen={isOpen}
			onAfterOpen={onAfterOpen}
			onAfterClose={onAfterClose}
			onRequestClose={onRequestClose}
			contentLabel={label}
			shouldCloseOnEsc={shouldCloseOnEsc}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			aria={
				{
					// Properly typed aria-live property
					// live: 'assertive' as const,
				}
			}
			{...modalProps}
		>
			{children}
		</ReactModal>
	);
}

const ObserverNewModal = observer(NewModalContainer);

interface HeaderProps extends HTMLAttributes<HTMLDivElement> {
	children?: React.ReactNode;
}

function Header({ children, className, ...rest }: HeaderProps) {
	return (
		<div className={classNames(modalClassNames.header, className)} {...rest}>
			{children}
		</div>
	);
}

interface TitleProps extends HTMLAttributes<HTMLHeadingElement> {
	children?: React.ReactNode;
}

function Title({ children, className, ...rest }: TitleProps) {
	return (
		<h4 className={classNames(modalClassNames.title, className)} {...rest}>
			{children}
		</h4>
	);
}

interface BodyProps extends HTMLAttributes<HTMLDivElement> {}

function Body({ children, className, ...rest }: BodyProps) {
	return (
		<div className={classNames(modalClassNames.message, className)} {...rest}>
			{children}
		</div>
	);
}

interface LabelProps {
	children?: React.ReactNode;
	className?: string;
}

function Label({ children, className }: LabelProps) {
	return <div className={classNames(modalClassNames.label, className)}>{children}</div>;
}

interface SectionProps {
	children?: React.ReactNode;
	className?: string;
}

function Section({ children, className }: SectionProps) {
	return <div className={classNames(modalClassNames.section, className)}>{children}</div>;
}

interface FooterProps extends HTMLAttributes<HTMLDivElement> {
	children?: React.ReactNode;
}

function Footer({ children, className, ...rest }: FooterProps) {
	return (
		<div className={classNames(modalClassNames.footer, className)} {...rest}>
			{children}
		</div>
	);
}

type CancelButtonProps = ICbButtonProps & ButtonHTMLAttributes<HTMLButtonElement>;

function CancelButton({ children, className, ...rest }: CancelButtonProps) {
	return (
		<Button
			className={classNames(modalClassNames.cancel, className)}
			display={Display.Outline}
			colors={Colors.Grey}
			newStyles
			{...rest}
		>
			{children}
		</Button>
	);
}

type ConfirmButtonProps = ICbButtonProps & ButtonHTMLAttributes<HTMLButtonElement>;

function ConfirmButton({ children, className, ...rest }: ConfirmButtonProps) {
	return (
		<Button
			className={classNames(modalClassNames.confirm, className)}
			display={Display.Solid}
			colors={Colors.Secondary}
			newStyles
			{...rest}
		>
			{children}
		</Button>
	);
}

interface NewModalComponents {
	Header: typeof Header;
	Title: typeof Title;
	Body: typeof Body;
	Label: typeof Label;
	Section: typeof Section;
	Footer: typeof Footer;
	CancelButton: typeof CancelButton;
	ConfirmButton: typeof ConfirmButton;
}

export const NewModal = Object.assign(ObserverNewModal, {
	Header,
	Title,
	Body,
	Label,
	Section,
	Footer,
	CancelButton,
	ConfirmButton,
}) as typeof ObserverNewModal & NewModalComponents;
